import { createAction, createSlice } from "@reduxjs/toolkit";
import { RootState } from "store/store";
import { ContentItemPayload, RatingContentState } from "../content/types";
import { RatingQuery } from "../type";

const initialState: RatingContentState = {
	list: [],
	total: 0,
	detail: null,
	contentItems: [],
	isSaved: false,
	customerRatings: [],
	customerRatingTotal: 0,
	merchantRatings: [],
	merchantRatingTotal: 0,
};

export const ratingSlice = createSlice({
	name: "rating",
	initialState,
	reducers: {
		setRatingContentList: (state, action) => {
			state.list = action.payload.data;
			if (action.payload?.total) {
				state.total = action.payload.total;
			}
		},
		setRatingContentDetail: (state, action) => {
			if(action.payload?.id) state.detail = action.payload;
			const newList = state.list.map((rating) => {
				if(rating.id === action.payload?.id) {
					return action.payload;
				}
				return rating;
			});
			state.list = newList;
		},
		setRatingContentDetailStatus: (state, action) => {
			if(state.detail) {
				state.detail = { ...state.detail, status: action.payload.status };
			}
			state.list = state.list.map(content => {
				if(action.payload.id === content.id) {
					return { ...content, status: action.payload.status };
				}
				return content;
			});
		},
		setContentItems: (state, action) => {
			state.contentItems = action.payload;
		},
		setContentItemsByRating: (state, action) => {
			const newContentItems = state.contentItems.map(items => {
				if (items[0]?.rate !== action.payload?.rate) return items;
				return action.payload.items;
			});
			state.contentItems = newContentItems;
		},
		setRatingContentIsSaved: (state, action) => {
			state.isSaved = action.payload;
		},
		setCustomerRatings: (state, action) => {
			state.customerRatings = action.payload;
		},
		setCustomerRatingTotal: (state, action) => {
			state.customerRatingTotal = action.payload;
		},
		setMerchantRatings: (state, action) => {
			state.merchantRatings = action.payload;
		},
		setMerchantRatingTotal: (state, action) => {
			state.merchantRatingTotal = action.payload;
		},
	},
});

export const selectRatingContentList = (state: RootState) =>
	state.rating.list;
export const selectRatingContentTotal = (state: RootState) =>
	state.rating.total;
export const selectRatingContentDetail = (state: RootState) =>
	state.rating.detail;
export const selectRatingContentItems = (state: RootState) =>
	state.rating.contentItems;
export const selectRatingContentIsSaved = (state: RootState) =>
	state.rating.isSaved;
export const selectCustomerRatings = (state: RootState) =>
	state.rating.customerRatings;
export const selectCustomerRatingTotal = (state: RootState) =>
	state.rating.customerRatingTotal;
export const selectMerchantRatings = (state: RootState) =>
	state.rating.merchantRatings;
export const selectMerchantRatingTotal = (state: RootState) =>
	state.rating.merchantRatingTotal;

export const {
	setRatingContentList,
	setRatingContentDetail,
	setRatingContentDetailStatus,
	setContentItems,
	setContentItemsByRating,
	setRatingContentIsSaved,
	setCustomerRatings,
	setCustomerRatingTotal,
	setMerchantRatings,
	setMerchantRatingTotal,
} = ratingSlice.actions;

export interface GetRatingContentsPayload {
	callback?: () => void;
}
export const getRatingContentsAsync = createAction(
	"rating/getRatingContentsAsync",
	(payload: GetRatingContentsPayload) => ({
		payload,
	}),
);

export interface GetRatingContentPayload {
	id: number;
	callback?: () => void;
}
export const getRatingContentAsync = createAction(
	"rating/getRatingContentAsync",
	(payload: GetRatingContentPayload) => ({
		payload,
	}),
);

export interface UpdateRatingContentPayload {
	id: number;
	items: ContentItemPayload[];
	callback?: () => void;
	successCallback?: () => void;
}
export const updateRatingContentAsync = createAction(
	"rating/updateRatingContentAsync",
	(payload: UpdateRatingContentPayload) => ({
		payload,
	}),
);

export interface UpdateRatingContentStatusPayload {
	id: number;
	status: boolean;
	callback?: () => void;
	successCallback?: () => void;
}
export const updateRatingContentStatusAsync = createAction(
	"rating/updateRatingContentStatusAsync",
	(payload: UpdateRatingContentStatusPayload) => ({
		payload,
	}),
);

export interface GetRatingsPayload {
	query: RatingQuery;
	language: string;
	callback?: () => void;
	successCallback?: () => void;
}
export const getRatingsAsync = createAction(
	"rating/getRatingsAsync",
	(payload: GetRatingsPayload) => ({
		payload,
	}),
);

export default ratingSlice.reducer;
